import React, { useEffect, useId, useRef } from "react";

/**
 * BdrCheckbox — the app's checkbox.
 *
 * The CSS lives in the global `index.css` (`.bdr-checkbox-*`, registry §26).
 * It replaces 27 hand-rolled controls across 17 files — 18 raw
 * `<input type="checkbox">`, 9 MUI `<Checkbox>`, and 22 local CSS recipes
 * between them.
 *
 *   <BdrCheckbox
 *     label="Show all items"
 *     checked={showAll}
 *     onChange={(e) => setShowAll(e.target.checked)}
 *   />
 *
 * THE REAL INPUT IS STILL THERE. It is visually hidden but not `display:
 * none` and not `hidden` — either of those drops it out of the tab order and
 * off the accessibility tree, which is how a checkbox stops being reachable by
 * keyboard while still looking fine. It stays focusable, Space still toggles
 * it, and a screen reader still announces it; the `<span>` beside it is
 * decoration that follows its state through CSS sibling selectors.
 *
 * The whole thing is a `<label>`, so the text is part of the hit target
 * without needing an id/htmlFor pair. One is generated anyway for callers that
 * need to point at it.
 */

const BdrCheckbox = ({
  checked,
  onChange,
  /** the text beside the box. Omit for a bare checkbox (a table row's select) */
  label,
  /** descriptive line under the label — never a validation message (§6c) */
  helper,
  disabled = false,
  /**
   * The "some but not all" state for a select-all. This is a DOM PROPERTY, not
   * an attribute — React cannot set it through JSX, so it goes on via a ref.
   * `checked` should be false while this is true.
   */
  indeterminate = false,
  name,
  value,
  id,
  className = "",
  /**
   * Tooltip. Goes on the LABEL, not the input — the input is scaled to nothing,
   * so a title on it could never be hovered.
   */
  title,
  /** "md" (default) | "sm" — sm for dense rows, e.g. a list of selectables */
  size = "md",
  inputRef,
  ariaLabel,
  ...rest
}) => {
  const autoId = useId();
  const controlId = id || `${name || "checkbox"}-${autoId}`;

  const localRef = useRef(null);

  useEffect(() => {
    const node = localRef.current;
    if (node) node.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  const setRefs = (node) => {
    localRef.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef) inputRef.current = node;
  };

  const rootClass = [
    "bdr-checkbox",
    size !== "md" ? `bdr-checkbox--${size}` : "",
    disabled ? "is-disabled" : "",
    /* One line of label sits CENTRED beside the box; a second line makes the
       box align to the first line instead. The class carries that distinction
       so the CSS does not have to guess at the label's height. */
    helper ? "has-helper" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <label className={rootClass} htmlFor={controlId} title={title}>
      <input
        ref={setRefs}
        id={controlId}
        className="bdr-checkbox-input"
        type="checkbox"
        name={name}
        value={value}
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={onChange}
        aria-label={!label ? ariaLabel : undefined}
        {...rest}
      />

      <span className="bdr-checkbox-box" aria-hidden="true">
        {/*
          Two marks in one box. The tick is drawn rather than faded in: its
          stroke is dashed to its own length and the offset animates to 0, so
          the line appears to be written. `pathLength="1"` makes that exact
          without anyone having to measure the path — the dash array is then
          just "1", whatever the real geometry is.
        */}
        <svg className="bdr-checkbox-mark" viewBox="0 0 24 24" focusable="false">
          <path
            className="bdr-checkbox-tick"
            pathLength="1"
            d="M5 12.5l4.7 4.7L19 7.4"
            fill="none"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            className="bdr-checkbox-dash"
            pathLength="1"
            d="M6.5 12h11"
            fill="none"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
        </svg>
      </span>

      {(label || helper) && (
        <span className="bdr-checkbox-text">
          {label && <span className="bdr-checkbox-label">{label}</span>}
          {helper && <span className="bdr-checkbox-helper">{helper}</span>}
        </span>
      )}
    </label>
  );
};

export default BdrCheckbox;
